import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { performanceMonitor } from '@/services/performanceMonitor';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRouteSEO } from '@/hooks/useRouteSEO';

interface AppInitializerProps {
  children: React.ReactNode;
}

const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  useAnalytics();
  useRouteSEO();
  useEffect(() => {
    // Mark app as loaded to hide HTML fallback
    document.body.classList.add('app-loaded');
    
    // Simple initialization with error handling
    const timer = setTimeout(() => {
      try {
        performanceMonitor.start();
        setIsReady(true);
        console.log('[AppInitializer] App ready');
        
        // Hide initial loader
        const initialLoader = document.getElementById('initial-loader');
        if (initialLoader) {
          initialLoader.style.display = 'none';
        }
      } catch (error) {
        console.error('[AppInitializer] Initialization failed:', error);
        setIsReady(true); // Still proceed to avoid infinite loading
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background flex items-center justify-center relative overflow-hidden">
        {/* Background animated elements */}
        <div className="absolute inset-0">
          {/* Floating orbs */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-primary/10 rounded-full blur-xl animate-pulse" />
          <div className="absolute top-40 right-32 w-24 h-24 bg-primary/5 rounded-full blur-lg animate-bounce" style={{ animationDelay: '0.5s' }} />
          <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-primary/8 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-20 right-20 w-28 h-28 bg-primary/6 rounded-full blur-xl animate-bounce" style={{ animationDelay: '1.5s' }} />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-primary/10" />
        </div>

        <div className="relative z-10 text-center space-y-8 px-8 max-w-md">
          {/* Logo Container with animation */}
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
            <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-primary/20 to-primary/10 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-primary/20 shadow-2xl">
              <img 
                src="/lovable-uploads/76503acc-0290-4b3e-ae3b-f6ab214ab2ce.png" 
                alt="Flamingo" 
                className="h-12 w-auto drop-shadow-lg animate-bounce"
                style={{ animationDuration: '2s' }}
              />
            </div>
          </div>

          {/* Loading text with gradient animation */}
          <div className="space-y-4">
            <h1 className="text-3xl font-bold font-heading bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent animate-pulse">
              Welcome to Flamingo
            </h1>
            <p className="text-muted-foreground text-lg animate-in fade-in-0 slide-in-from-bottom-4 duration-1000">
              Creating your perfect rental experience...
            </p>
          </div>

          {/* Animated loading bar */}
          <div className="space-y-3">
            <div className="relative w-full h-2 bg-muted/30 rounded-full overflow-hidden backdrop-blur-sm">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-primary rounded-full animate-pulse" />
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/80 rounded-full shadow-lg"
                style={{
                  width: '60%',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}
              />
            </div>
            <p className="text-sm text-muted-foreground animate-pulse">
              Setting up your dashboard and preferences
            </p>
          </div>

          {/* Floating dots animation */}
          <div className="flex justify-center space-x-2">
            <div className="w-3 h-3 bg-primary/60 rounded-full animate-bounce" />
            <div className="w-3 h-3 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
            <div className="w-3 h-3 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AppInitializer;
